<script setup>
// One Stage canonical MessageBubble primitive.
//
// Purpose: the visual surface for a single message inside a conversation thread.
// Replaces the legacy `Bubble.vue` / inline message-content markup found across
// `components/widgets/conversation/`. This file is FOUNDATION ONLY — no
// conversation-pane file consumes it yet (per Batch 3B scope).
//
// Composition contract:
//   <MessageBubble
//     variant="incoming"
//     :grouped="grouped"
//     :selected="selected"
//     timestamp="9:42 AM"
//     :avatar="{ src, name, status }"
//   >
//     <!-- default slot: message body / rich content -->
//     <template #attachments> … </template>
//     <template #reactions>   … </template>
//     <template #actions>     … </template>
//     <template #meta>        … </template>
//   </MessageBubble>
//
// Variants:
//   - incoming      → left-aligned, panel surface, slate text
//   - outgoing      → right-aligned, brand-tinted surface, on-brand text
//   - private       → left-aligned, amber-tinted surface (private notes)
//   - system        → centered, transparent, micro typography (events)
//   - whisper       → italic, slate-11 (private comments)
//
// All visuals consume One Stage tokens (n-* + os-* layer). Zero raw hex.

import { computed, useSlots } from 'vue';
import Avatar from '../avatar/Avatar.vue';

const props = defineProps({
  /** Bubble variant. */
  variant: {
    type: String,
    default: 'incoming',
    validator: v => ['incoming', 'outgoing', 'private', 'system', 'whisper'].includes(v),
  },
  /**
   * When true, this bubble is part of a vertical group with adjacent same-sender
   * messages. Group bubbles share a tighter rounded-radius on the abutting edge,
   * and the avatar is hidden on all but the first bubble of the group.
   * Pass 'first' / 'middle' / 'last' for precise control. `true` is treated as 'middle'.
   */
  grouped: {
    type: [Boolean, String],
    default: false,
    validator: v => v === false || v === true || ['first', 'middle', 'last'].includes(v),
  },
  /** Timestamp string shown in the meta region. */
  timestamp: { type: String, default: '' },
  /** Compact avatar descriptor — passed straight through to Avatar primitive. */
  avatar: {
    type: Object,
    default: () => null, // { src?, name, status?, size? }
  },
  /** Visual selection state — used by bulk-action surfaces. */
  selected: { type: Boolean, default: false },
  /** Disable hover-elevate transition (e.g. when host is virtualised). */
  staticHover: { type: Boolean, default: false },
  /** Sender display name (rendered above the bubble for incoming variant). */
  senderName: { type: String, default: '' },
  /** Sender role / metadata badge text — shown next to senderName. */
  senderMeta: { type: String, default: '' },
});

defineEmits(['select', 'click']);

const slots = useSlots();

const groupedPos = computed(() => {
  if (props.grouped === true) return 'middle';
  if (props.grouped === false) return 'standalone';
  return props.grouped;
});

const showAvatar = computed(
  () =>
    props.avatar &&
    (groupedPos.value === 'standalone' || groupedPos.value === 'first')
);

const isOutgoing = computed(() => props.variant === 'outgoing');
const isSystem   = computed(() => props.variant === 'system');
const isWhisper  = computed(() => props.variant === 'whisper');

// Per-variant surface classes. All theme-aware via n-* tokens.
const variantSurface = computed(() => ({
  incoming: 'bg-n-solid-1 dark:bg-n-solid-2 text-n-slate-12 border border-n-weak',
  outgoing: 'bg-n-brand text-white border border-n-brand',
  private:  'bg-n-amber-9/[0.10] text-n-slate-12 border border-n-amber-9/30 dark:bg-n-amber-9/[0.14]',
  system:   'bg-transparent text-n-slate-11 border-none',
  whisper:  'bg-transparent text-n-slate-11 italic border-none',
}[props.variant]));

// Per-group-position radius adjustments — tighten the side abutting the next
// bubble in the same sender's stack. Standalone bubbles get full rounding.
const radiusClass = computed(() => {
  if (isSystem.value || isWhisper.value) return '';
  const base = 'os-r-xl'; // 14px
  const side = isOutgoing.value ? 'rounded-tr' : 'rounded-tl';
  const tailSide = isOutgoing.value ? 'rounded-br' : 'rounded-bl';
  switch (groupedPos.value) {
    case 'first':  return `${base} ${tailSide}-sm`;
    case 'middle': return `${base} ${side}-sm ${tailSide}-sm`;
    case 'last':   return `${base} ${side}-sm`;
    default:       return base;
  }
});

const alignmentClass = computed(() => {
  if (isSystem.value) return 'justify-center';
  return isOutgoing.value ? 'justify-end' : 'justify-start';
});
</script>

<template>
  <article
    :class="[
      'group/bubble flex w-full gap-2 os-tx-colors',
      alignmentClass,
      groupedPos === 'first' || groupedPos === 'standalone' ? 'mt-3' : 'mt-0.5',
    ]"
    :aria-label="senderName || variant"
    @click="$emit('click', $event)"
  >
    <!-- Leading avatar slot — incoming + private + whisper only -->
    <div
      v-if="!isOutgoing && !isSystem"
      class="flex-shrink-0"
      :style="{ width: avatar ? '32px' : '0' }"
    >
      <Avatar
        v-if="showAvatar"
        v-bind="avatar"
        :size="avatar?.size || 32"
        rounded-full
      />
    </div>

    <!-- Bubble column (header + bubble + meta) -->
    <div
      :class="[
        'flex flex-col min-w-0 max-w-[min(640px,82%)]',
        isOutgoing && 'items-end',
      ]"
    >
      <!-- Sender header — shown for incoming first/standalone only -->
      <header
        v-if="
          (senderName || senderMeta) &&
          !isOutgoing &&
          (groupedPos === 'first' || groupedPos === 'standalone') &&
          !isSystem
        "
        class="flex items-baseline gap-2 mb-1 px-1"
      >
        <span class="text-xs font-medium text-n-slate-12">
          {{ senderName }}
        </span>
        <span v-if="senderMeta" class="text-[11px] text-n-slate-11">
          {{ senderMeta }}
        </span>
      </header>

      <!-- The bubble itself -->
      <div
        :class="[
          'relative px-3.5 py-2 leading-relaxed text-sm os-tx-shadow',
          variantSurface,
          radiusClass,
          isSystem && 'text-xs text-center py-1',
          isWhisper && 'text-xs',
          selected && 'os-elev-2 ring-2 ring-n-brand/40',
          !staticHover && !isSystem && 'hover:os-elev-1',
        ]"
        :aria-selected="selected || undefined"
        @click.stop="$emit('select', $event)"
      >
        <!-- Default slot — rich message body. -->
        <div class="min-w-0 break-words">
          <slot />
        </div>

        <!-- Attachments region -->
        <div
          v-if="slots.attachments"
          class="mt-2 flex flex-wrap gap-2"
        >
          <slot name="attachments" />
        </div>

        <!-- Reactions region — inline below the body, smaller spacing -->
        <div
          v-if="slots.reactions"
          class="mt-1.5 flex flex-wrap gap-1"
        >
          <slot name="reactions" />
        </div>

        <!-- Hover-revealed actions toolbar (e.g. reply, copy, edit) -->
        <div
          v-if="slots.actions"
          :class="[
            'absolute -top-3 flex items-center gap-0.5 px-1 py-0.5 os-r-pill bg-n-solid-1 dark:bg-n-solid-3 border border-n-weak os-elev-2 opacity-0 transition-opacity duration-150',
            isOutgoing ? 'right-1' : 'left-1',
            'group-hover/bubble:opacity-100 focus-within:opacity-100',
          ]"
        >
          <slot name="actions" />
        </div>
      </div>

      <!-- Meta / timestamp footer — shown for last/standalone bubbles only -->
      <footer
        v-if="
          (timestamp || slots.meta) &&
          (groupedPos === 'last' || groupedPos === 'standalone') &&
          !isSystem
        "
        :class="[
          'mt-1 px-1 flex items-center gap-2 text-[11px] text-n-slate-11',
          isOutgoing ? 'flex-row-reverse' : 'flex-row',
        ]"
      >
        <span v-if="timestamp">{{ timestamp }}</span>
        <slot name="meta" />
      </footer>
    </div>
  </article>
</template>
